import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight, BellOff, ShieldCheck, Clock } from 'lucide-react'

export const Route = createFileRoute('/_site/blog/why-alert-confirmation')({
  component: WhyAlertConfirmationPage,
  head: () => ({
    meta: [
      { title: 'Why Most Uptime Alerts Are Noise' },
      { name: 'description', content: 'Single-check monitoring fires on every DNS timeout and CDN blip. Why Uptrack alerts only after 2–5 consecutive failed checks.' },
      { property: 'og:title', content: 'Why Most Uptime Alerts Are Noise — Uptrack' },
      { property: 'og:description', content: 'Up to 90% of monitoring alerts are false positives. Consecutive-check confirmation fixes this. Here\'s what we believe.' },
      { property: 'og:image', content: 'https://uptrack.app/og/blog-why-alert-confirmation.png' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: 'https://uptrack.app/blog/why-alert-confirmation' },
      { property: 'article:published_time', content: '2026-04-01' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/blog/why-alert-confirmation' },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: 'Why Most Uptime Alerts Are Noise — And How to Fix It',
          description: 'Single-check monitoring fires on every DNS timeout, CDN blip, and network hiccup. Consecutive-check confirmation cuts false alerts to near zero.',
          datePublished: '2026-04-01',
          dateModified: '2026-05-22',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: { '@type': 'Organization', name: 'Uptrack', url: 'https://uptrack.app', logo: { '@type': 'ImageObject', url: 'https://uptrack.app/uptrack-logo-header.png' } },
          mainEntityOfPage: 'https://uptrack.app/blog/why-alert-confirmation',
        }),
      },
    ],
  }),
})

function WhyAlertConfirmationPage() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <p className="mb-3 text-sm text-muted-foreground">April 1, 2026</p>
          <h1 className="font-sans mb-6 text-4xl font-semibold">
            Why most uptime alerts are noise — and how to fix it
          </h1>

          {/* Hook */}
          <section className="mb-12">
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Your phone buzzes at 3am. DNS timeout. By the time you open your laptop, pull up the dashboard, and squint at the screen, it's already resolved. The site was never actually down. You go back to sleep, heart still racing.
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              This happens three times a week. Eventually, you start ignoring alerts. And that's when the real outage hits, the one that lasts 47 minutes before anyone notices because the whole team has been conditioned to assume every alert is a false alarm.
            </p>
          </section>

          {/* The problem */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">The false positive epidemic</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Most uptime monitoring tools work the same way: send a request, check the response, fire an alert if anything looks wrong. One failed check equals one alert. Simple, right?
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              The problem is that the internet is noisy. DNS resolvers hiccup. CDN edge nodes rotate. A random TCP packet gets dropped. Your monitoring provider's own network has a blip. None of these mean your site is down. But they all trigger alerts.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Industry data paints a grim picture. Studies from PagerDuty and Atlassian consistently show that 50-90% of monitoring alerts are false positives. Some teams report alert fatigue so severe that they've muted channels entirely. The tool designed to catch outages becomes the thing that ensures outages get missed.
            </p>
            <div className="rounded-lg border border-border bg-card p-5">
              <p className="text-sm font-medium text-muted-foreground">Common causes of false alerts:</p>
              <div className="mt-3 space-y-2">
                {[
                  'DNS resolver timeout (usually resolves in under 5 seconds)',
                  'CDN edge node returning a stale error during rotation',
                  'Transient network path issue between monitoring server and your origin',
                  'TLS handshake timeout under momentary load spike',
                  'Rate limiting from an overly aggressive WAF rule',
                ].map((cause, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <span className="mt-0.5 text-status-down">x</span>
                    <span className="text-muted-foreground">{cause}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-lg leading-[1.7] text-body">
                Every one of these will trigger an alert with single-check monitoring. None of them mean your customers are experiencing downtime.
              </p>
            </div>
          </section>

          {/* What competitors do */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">The industry's default is broken</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              UptimeRobot, Pingdom, StatusCake, Better Stack — almost every monitoring tool on the market alerts on the first failure by default. Some of them offer a "confirm from another region" option, where a second check runs from a different data center before alerting. That's better than nothing, but it's still a single point-in-time verification.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              A DNS timeout in Virginia and a DNS timeout in Frankfurt can happen simultaneously for reasons that have nothing to do with your server. A CDN propagation issue can affect multiple regions at once. Checking from two places at the same moment tells you the internet is being the internet, not that your application is broken.
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              And here's the thing: these providers know this. They've known for years. But single-check alerting means more alerts, and more alerts feel like the tool is "doing something." It's a feature that looks good in demos and creates misery in production.
            </p>
          </section>

          {/* What consecutive-check confirmation is */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Consecutive-check confirmation: the fix</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              The idea is simple. Instead of alerting on a single failure, require multiple consecutive failures before considering it a real outage. Not a second opinion from another region at the same moment — sequential checks over time.
            </p>

            <div className="my-6 rounded-lg border border-border bg-card p-6">
              <p className="mb-4 text-sm font-medium">How it works:</p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-status-down/20 text-sm font-semibold text-status-down">1</div>
                  <div>
                    <p className="font-medium">Check fails</p>
                    <p className="text-lg leading-[1.7] text-body">DNS timeout. Could be real, could be noise. Don't alert yet — immediately re-check.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-status-degraded/20 text-sm font-semibold text-status-degraded">2</div>
                  <div>
                    <p className="font-medium">Second check runs</p>
                    <p className="text-lg leading-[1.7] text-body">If it passes, the first failure was transient. Discard it. No alert, no noise. If it fails again, escalate.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-status-down/20 text-sm font-semibold text-status-down">3</div>
                  <div>
                    <p className="font-medium">Third consecutive failure</p>
                    <p className="text-lg leading-[1.7] text-body">Three failures in a row. This isn't a blip — something is actually wrong. Now alert.</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 rounded border border-border bg-background/50 p-3">
                <p className="font-mono text-xs text-muted-foreground">
                  <span className="text-status-down">FAIL</span> {'->'}
                  <span className="text-status-up"> PASS</span> {'->'}
                  <span className="text-muted-foreground"> (no alert, transient failure)</span>
                </p>
                <p className="mt-1 font-mono text-xs text-muted-foreground">
                  <span className="text-status-down">FAIL</span> {'->'}
                  <span className="text-status-down"> FAIL</span> {'->'}
                  <span className="text-status-down"> FAIL</span> {'->'}
                  <span className="text-muted-foreground"> ALERT: confirmed outage</span>
                </p>
              </div>
            </div>

            <p className="text-lg leading-[1.7] text-muted-foreground">
              You configure how many consecutive failures you need — 2, 3, or 5, depending on how cautious you want to be. The checks run back-to-back with no delay between them, so confirmation is fast.
            </p>
          </section>

          {/* Why this works */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Why this works so well</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Transient failures have a defining characteristic: they resolve themselves. A DNS timeout at 03:00:00 is gone by 03:00:05. A CDN error during edge rotation clears in seconds. Network path issues between two specific points are ephemeral by nature.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Real outages have the opposite characteristic: they persist. Your server crashed and isn't coming back without intervention. Your database ran out of connections. Your deployment broke the health check endpoint. These failures don't resolve themselves in 30 seconds. They show up on check 1, check 2, check 3, and they keep showing up.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-border bg-card p-5">
                <BellOff className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Near-zero false alerts</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Transient failures almost never repeat 3x in a row. Your phone only buzzes when something is genuinely broken.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <ShieldCheck className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Alerts you trust</h3>
                <p className="text-lg leading-[1.7] text-body">
                  When your team sees an Uptrack alert, they act on it. No more "probably just another false positive" dismissals.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Clock className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Faster response</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Paradoxically, slower alerting leads to faster response. Teams that trust their alerts respond immediately instead of waiting to see if it clears.
                </p>
              </div>
            </div>
          </section>

          {/* The tradeoff */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">The tradeoff we're happy to make</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Let's be honest about the cost. Consecutive-check confirmation means your alert arrives 30-60 seconds later than single-check monitoring. If your check interval is 30 seconds and you require 3 consecutive failures, you'll know about a real outage roughly 90 seconds after it starts, instead of 30 seconds.
            </p>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              For most teams, that tradeoff isn't even close. An extra minute of detection time versus hundreds of false alerts per month? Every on-call engineer we've talked to takes the same deal. Sleep matters. Trust in your tools matters. The mental overhead of triaging every buzz and beep matters.
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              If you're running high-frequency trading infrastructure where every millisecond counts, single-check alerting might make sense for you. For the other 99% of teams running web apps, APIs, and SaaS products, consecutive-check confirmation is strictly better.
            </p>
          </section>

          {/* Our stance */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">What we built and why</h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              We built Uptrack with consecutive-check confirmation as a default, not an add-on. Every monitor, on every plan, uses this approach. You can configure the threshold (2, 3, or 5 consecutive failures), but you can't turn it off entirely, because alerting on a single failure is almost always wrong.
            </p>
            <p className="text-lg leading-[1.7] text-muted-foreground">
              This is an opinionated choice, and we're comfortable with it. We'd rather give you 10 real alerts per year than 500 false ones. We'd rather you trust the system enough to actually wake up when it buzzes at 3am, because you know it means something.
            </p>
          </section>

          {/* CTA */}
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <BellOff className="mx-auto mb-4 h-8 w-8 text-primary" />
            <h2 className="font-sans mb-3 text-xl font-semibold">Tired of false alerts?</h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              All monitors at 30-second checks, from $3/mo. Consecutive-check confirmation on every plan. No surprise 3am wake-ups from DNS blips. 30-day money-back guarantee.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See Plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </article>
      </>

      </>
  )
}
